// AssignmentComposer — create a work assignment from a conversation. Anyone can create
// a task for themselves; managers can also assign it to a colleague (the assignee picker
// only appears for them — the server re-checks and enforces team scope). Posts an
// assignment card into the thread.
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Directory } from '../hooks/useDirectory';

interface Props {
  directory: Directory;
  selfId?: string;
  canAssign: boolean; // manager+ → may assign to others
  onCreate: (input: { title: string; description?: string; priority?: string; dueAt?: string; assigneeEmployeeId?: string; assigneeName?: string }) => Promise<void>;
  onClose: () => void;
}

// ⚠️ DOMAIN values, not labels. work-management's CHECK constraint accepts
// low | normal | high | urgent; this list said "medium", which the database
// rejects — so every assignment failed with a constraint violation that
// surfaced as "Request failed with status code 502". The label people see
// comes from i18n (assignment.p_normal still reads "Medium").
const PRIORITIES = ['low', 'normal', 'high'] as const;

export const AssignmentComposer: React.FC<Props> = ({ directory, selfId, canAssign, onCreate, onClose }) => {
  const { t } = useTranslation('conversations');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<(typeof PRIORITIES)[number]>('normal');
  const [due, setDue] = useState('');
  const [assignee, setAssignee] = useState<{ employeeId: string; name: string } | null>(null);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const people = useMemo(
    () => directory.search(q).filter((p) => p.userId !== selfId && !!p.employeeId).slice(0, 8),
    [directory, q, selfId],
  );

  const submit = async () => {
    const name = title.trim();
    if (!name || busy) return;
    setBusy(true);
    setErr('');
    try {
      await onCreate({
        title: name,
        description: description.trim() || undefined,
        priority,
        dueAt: due ? new Date(due + 'T17:00:00').toISOString() : undefined,
        assigneeEmployeeId: assignee?.employeeId,
        assigneeName: assignee?.name,
      });
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : t('assignment.failed'));
      setBusy(false);
    }
  };

  return (
    <div className="absolute bottom-full left-0 z-20 mb-2 w-80 rounded-xl border border-border bg-card p-3 shadow-lg">
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-sm font-semibold text-charcoal">{t('assignment.title')}</h4>
        <button type="button" onClick={onClose} className="text-muted-foreground hover:text-charcoal" aria-label={t('edit.cancel')}>
          ✕
        </button>
      </div>
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        autoFocus
        placeholder={t('assignment.what')}
        className="mb-2 w-full rounded-lg border border-border bg-background px-2.5 py-1.5 text-sm text-charcoal focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-200"
      />
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder={t('assignment.details')}
        rows={2}
        className="mb-2 w-full resize-none rounded-lg border border-border bg-background px-2.5 py-1.5 text-sm text-charcoal focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-200"
      />
      <div className="mb-2 flex gap-2">
        <label className="flex-1 text-xs text-muted-foreground">
          {t('assignment.due')}
          <input
            type="date"
            value={due}
            onChange={(e) => setDue(e.target.value)}
            className="mt-0.5 w-full rounded-lg border border-border bg-background px-2 py-1 text-sm text-charcoal focus:outline-none"
          />
        </label>
        <label className="flex-1 text-xs text-muted-foreground">
          {t('assignment.priority')}
          <select
            value={priority}
            onChange={(e) => setPriority(e.target.value as typeof priority)}
            className="mt-0.5 w-full rounded-lg border border-border bg-background px-2 py-1 text-sm capitalize text-charcoal focus:outline-none"
          >
            {PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {t(`assignment.p_${p}`)}
              </option>
            ))}
          </select>
        </label>
      </div>

      {canAssign ? (
        <div className="mb-2">
          <p className="mb-1 text-xs text-muted-foreground">{t('assignment.assign_to')}</p>
          {assignee ? (
            <div className="flex items-center justify-between rounded-lg bg-muted px-2 py-1.5 text-sm text-charcoal">
              <span className="truncate">{assignee.name}</span>
              <button type="button" onClick={() => setAssignee(null)} className="text-xs text-primary-600">
                {t('assignment.change')}
              </button>
            </div>
          ) : (
            <>
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t('assignment.search_people')}
                className="w-full rounded-lg border border-border bg-background px-2.5 py-1.5 text-sm text-charcoal focus:outline-none focus:ring-1 focus:ring-primary-200"
              />
              {q.trim() && (
                <ul className="mt-1 max-h-36 overflow-y-auto rounded-lg border border-border">
                  {people.length === 0 ? (
                    <li className="px-2 py-2 text-xs text-muted-foreground">{t('assignment.no_people')}</li>
                  ) : (
                    people.map((p) => (
                      <li key={p.userId}>
                        <button
                          type="button"
                          onClick={() => {
                            setAssignee({ employeeId: p.employeeId as string, name: p.name });
                            setQ('');
                          }}
                          className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-sm hover:bg-muted"
                        >
                          <span className="truncate text-charcoal">{p.name}</span>
                          {p.position && <span className="truncate text-xs text-muted-foreground">· {p.position}</span>}
                        </button>
                      </li>
                    ))
                  )}
                </ul>
              )}
            </>
          )}
        </div>
      ) : (
        <p className="mb-2 text-xs text-muted-foreground">{t('assignment.self_only')}</p>
      )}

      {err && <p className="mb-2 text-xs text-red-600">{err}</p>}
      <button
        type="button"
        onClick={submit}
        disabled={!title.trim() || busy}
        className="w-full rounded-lg bg-primary-500 px-3 py-2 text-sm font-semibold text-white hover:bg-primary-600 disabled:opacity-50"
      >
        {busy ? t('assignment.creating') : assignee ? t('assignment.create_assign') : t('assignment.create')}
      </button>
    </div>
  );
};
